import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  MenuItem,
  CircularProgress,
  IconButton,
  InputAdornment,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export default function EditStaffModal({ open, staff, onClose, onSave }) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    username: '',
    newPassword: '',
    designation: '',
    staffType: 'Task-Based',
    monthlySalary: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (staff) {
      setFormData({
        fullName: staff.FullName || '',
        email: staff.Email || '',
        phone: staff.Phone || staff.PhoneNumber || '',
        username: staff.Username || '',
        newPassword: '',
        designation: staff.Designation || '',
        staffType: staff.StaffType || 'Task-Based',
        monthlySalary: staff.MonthlySalary || '',
      });
      setErrors({});
    }
  }, [staff]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    // Validate password only if provided
    if (formData.newPassword && formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters long';
    }

    if (!formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
    }

    if (!formData.monthlySalary) {
      newErrors.monthlySalary = 'Monthly salary is required';
    } else if (parseFloat(formData.monthlySalary) <= 0) {
      newErrors.monthlySalary = 'Salary must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving staff:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!staff) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          bgcolor: '#2a2a2a',
          border: '1px solid #444',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            bgcolor: '#ed6c02',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Edit Staff - {staff.FullName}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, bgcolor: '#2a2a2a' }}>
          <Grid container spacing={3}>
            {/* Staff Information Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C4A574', mb: 1 }}>
                Staff Information
              </Typography>
              <Divider sx={{ mb: 2, borderColor: '#444' }} />
            </Grid>

            {/* Read-only fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Staff ID"
                value={staff.StaffId}
                disabled
                variant="filled"
                size="small"
                sx={{
                  '& .MuiFilledInput-root': {
                    bgcolor: '#1a1a1a',
                    '&:hover': { bgcolor: '#1a1a1a' },
                    '&.Mui-focused': { bgcolor: '#1a1a1a' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiFilledInput-input': { color: '#fff' },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Joining Date"
                value={
                  staff.JoiningDate
                    ? new Date(staff.JoiningDate).toLocaleDateString('en-IN')
                    : 'N/A'
                }
                disabled
                variant="filled"
                size="small"
                sx={{
                  '& .MuiFilledInput-root': {
                    bgcolor: '#1a1a1a',
                    '&:hover': { bgcolor: '#1a1a1a' },
                    '&.Mui-focused': { bgcolor: '#1a1a1a' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiFilledInput-input': { color: '#fff' },
                }}
              />
            </Grid>

            {/* Personal Details Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C4A574', mb: 1, mt: 2 }}>
                Personal Details
              </Typography>
              <Divider sx={{ mb: 2, borderColor: '#444' }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                error={!!errors.fullName}
                helperText={errors.fullName}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1a1a1a',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-input': { color: '#fff' },
                  '& .MuiFormHelperText-root': { color: '#f44336' },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1a1a1a',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-input': { color: '#fff' },
                  '& .MuiFormHelperText-root': { color: '#f44336' },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={!!errors.phone}
                helperText={errors.phone}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1a1a1a',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-input': { color: '#fff' },
                  '& .MuiFormHelperText-root': { color: '#f44336' },
                }}
              />
            </Grid>

            {/* Account Credentials Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C4A574', mb: 1, mt: 2 }}>
                Account Credentials
              </Typography>
              <Divider sx={{ mb: 2, borderColor: '#444' }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                error={!!errors.username}
                helperText={errors.username || "Staff can login with this username"}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1a1a1a',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-input': { color: '#fff' },
                  '& .MuiFormHelperText-root': { color: errors.username ? '#f44336' : '#ccc' },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="New Password (Optional)"
                name="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleChange}
                error={!!errors.newPassword}
                helperText={errors.newPassword || "Leave blank to keep current password"}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1a1a1a',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-input': { color: '#fff' },
                  '& .MuiFormHelperText-root': { color: errors.newPassword ? '#f44336' : '#ccc' },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: '#ccc' }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Employment Details Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C4A574', mb: 1, mt: 2 }}>
                Employment Details
              </Typography>
              <Divider sx={{ mb: 2, borderColor: '#444' }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                error={!!errors.designation}
                helperText={errors.designation}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1a1a1a',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-input': { color: '#fff' },
                  '& .MuiFormHelperText-root': { color: '#f44336' },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                select
                label="Staff Type"
                name="staffType"
                value={formData.staffType}
                onChange={handleChange}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1a1a1a',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-input': { color: '#fff' },
                  '& .MuiSelect-icon': { color: '#ccc' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#2a2a2a',
                      '& .MuiMenuItem-root': {
                        color: '#fff',
                        '&:hover': { bgcolor: '#3a3a3a' },
                        '&.Mui-selected': { bgcolor: '#C4A574', '&:hover': { bgcolor: '#B39562' } },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="Task-Based">Task-Based</MenuItem>
                <MenuItem value="Hourly">Hourly</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Monthly Salary"
                name="monthlySalary"
                type="number"
                value={formData.monthlySalary}
                onChange={handleChange}
                error={!!errors.monthlySalary}
                helperText={errors.monthlySalary}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1a1a1a',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#C4A574' },
                    '&.Mui-focused fieldset': { borderColor: '#C4A574' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-input': { color: '#fff' },
                  '& .MuiFormHelperText-root': { color: '#f44336' },
                }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1, color: '#ccc' }}>₹</Typography>,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: '#1a1a1a', gap: 1, borderTop: '1px solid #444' }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
            sx={{
              color: '#ccc',
              fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: '#ed6c02',
              color: 'white',
              fontWeight: 600,
              px: 3,
              '&:hover': { bgcolor: '#e65100' },
              '&:disabled': { bgcolor: '#555' }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
